import React, { useState, useMemo } from "react";
import { Menu, MenuItem } from "@headlessui/react";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import CustomTable from "./CustomTable";
import { formatTimeAgo } from "../utils/chat";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

/** Active Status: green dot + last login time (or "Active") / gray dot + "Inactive" */
export const ActiveStatusCell = ({ isActive, lastActivityAt }) => {
  const timeAgo = formatTimeAgo(lastActivityAt);
  const label = isActive ? (timeAgo || "Active") : "Inactive";
  return (
    <div className="flex min-w-0 items-center gap-2">
      <div className={`flex-none rounded-full p-1 ${isActive ? "bg-green-100" : "bg-gray-100"}`}>
        <div className={`h-1.5 w-1.5 rounded-full ${isActive ? "bg-green-600" : "bg-gray-600"}`} />
      </div>
      <span
        className={`min-w-0 break-words text-sm font-medium ${isActive ? "text-green-700" : "text-gray-700"}`}
      >
        {label}
      </span>
    </div>
  );
};

/** Date format for Created column (e.g. "Jan 15, 2024") */
export function formatCreatedDate(isoString) {
  if (!isoString) return "—";
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
}

/** Request Contact: badge "Primary" (purple border) or "Yes" (gray) or "–" */
export const RequestContactBadge = ({ value }) => {
  const v = (value || "").toString().trim();
  if (v === "Primary") {
    return (
      <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10">
        Primary
      </span>
    );
  }
  if (v === "Yes") {
    return (
      <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10">
        Yes
      </span>
    );
  }
  return <span className="text-gray-500">–</span>;
};

/**
 * Generic action dropdown: renders a kebab menu with items from actionMenuItems.
 * Each item: { label, icon: ReactNode, onClick: (row) => void }
 */
export const ActionMenuDropdown = ({ row, actionMenuItems }) => {
  if (!actionMenuItems?.length) return null;
  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="inline-flex justify-center rounded-md p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-50 focus:outline-none">
        <span className="sr-only">Open actions</span>
        <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
      </Menu.Button>
      <Menu.Items
        anchor="bottom end"
        className="z-[100] mt-2 w-56 origin-top-right rounded-lg border border-gray-200 bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none shadow-ring"
      >
        <div className="py-1">
          {actionMenuItems.map((item, idx) => (item?.showItem === undefined || item.showItem(row)) && (
            <MenuItem key={idx}>
              {({ focus }) => (
                <button
                  type="button"
                  onClick={() => item.onClick(row)}
                  className={classNames(
                    focus ? "bg-gray-50" : "",
                    "flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 text-gray-900 group font-medium"
                  )}
                >
                  {item.icon && <span className="flex-shrink-0 text-gray-400">{item.icon}</span>}
                  {item.label}
                </button>
              )}
            </MenuItem>
          ))}
        </div>
      </Menu.Items>
    </Menu>
  );
};

/**
 * Reusable table: columns, data, and action menu items must be passed by the parent.
 * No default columns or default action menu.
 */
const UsersListTable = ({
  columns: columnsProp,
  data,
  actionMenuItems = [],
  sortConfig: sortConfigProp = {},
  onSort: onSortProp,
  emptyMessage = "No data found",
  emptyDescription = "No records available.",
  isSearchActive = false,
  className = "",
  getRowClassName,
  rowKey = "id",
}) => {
  const rawItems = Array.isArray(data) ? data : [];

  const columns = useMemo(() => {
    const cols = [...(columnsProp || [])];
    if (actionMenuItems?.length) {
      cols
      .push({
        key: "actions",
        header: "",
        align: "right",
        sortable: false,
        width: "4%",
        cellClassName: "text-gray-500",
        /** Opacity on <tr> would dim the dropdown; row styling skips this cell */
        excludeFromRowClassName: true,
        render: (row) => <ActionMenuDropdown row={row} actionMenuItems={actionMenuItems} />,
      });
    }
    return cols.filter((col) => col.showItem === undefined || col.showItem === true);
  }, [columnsProp, actionMenuItems]);

  const [internalSortConfig, setInternalSortConfig] = useState(sortConfigProp);
  const handleSort = (key) => {
    if (onSortProp) {
      onSortProp(key);
      return;
    }
    setInternalSortConfig((prev) => ({
      [key]: { direction: prev[key]?.direction === "asc" ? "desc" : "asc" },
    }));
  };
  const effectiveSortConfig = onSortProp ? sortConfigProp : internalSortConfig;
  const sortKey = Object.keys(effectiveSortConfig)[0];
  const sortDirection = sortKey ? effectiveSortConfig[sortKey]?.direction : null;
  const sortedItems = useMemo(() => {
    if (!rawItems.length || !sortKey || !sortDirection) return rawItems;
    return [...rawItems].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal == null) return sortDirection === "asc" ? 1 : -1;
      if (bVal == null) return sortDirection === "asc" ? -1 : 1;
      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal, undefined, { sensitivity: "base" })
          : bVal.localeCompare(aVal, undefined, { sensitivity: "base" });
      }
      if (typeof aVal === "boolean" && typeof bVal === "boolean") {
        const aN = aVal ? 1 : 0;
        const bN = bVal ? 1 : 0;
        return sortDirection === "asc" ? aN - bN : bN - aN;
      }
      if (aVal instanceof Date || bVal instanceof Date) {
        const aT = new Date(aVal).getTime();
        const bT = new Date(bVal).getTime();
        return sortDirection === "asc" ? aT - bT : bT - aT;
      }
      return sortDirection === "asc" ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
  }, [rawItems, sortKey, sortDirection]);

  return (
    <div className="w-full min-w-0 max-w-full">
      <CustomTable
        columns={columns}
        data={sortedItems}
        sortConfig={effectiveSortConfig}
        onSort={handleSort}
        emptyMessage={emptyMessage}
        emptyDescription={
          isSearchActive
            ? "Your search did not match any records. Please try again."
            : emptyDescription
        }
        isSearchActive={isSearchActive}
        className={className}
        getRowClassName={getRowClassName}
        edgeToEdge={false}
        rowKey={rowKey}
      />
    </div>
  );
};

export default UsersListTable;
